import {useTranslation} from 'react-i18next';
import {useSelectedDocContext, useStateFacets} from '../../../../../search-hooks';
import {useSelector} from 'react-redux';
import React, {memo} from 'react';
import {SelectionType} from '../../../../../../facets/facetTypes';
import {EditableTag} from '../../editable-tag/editableTag';
import classNames from 'clsx';
import {buildDefaultFieldTitle, isEmptyArray} from '../../../../../../utils/utils';
import {Translations} from '../../../../../../backendTranslations/backendTranslations-types';

export const MetadataList = memo(() => {
    const {t} = useTranslation();
    const searchConfig = useSelector((state) => state.config.search);
    const facetState = useStateFacets();
    const selectedDoc = useSelectedDocContext();
    const translations: Translations = useSelector((state) => state.translations);

    if (!selectedDoc) {
        return null;
    }

    const metadata = selectedDoc.metadata;

    const metadataFacets = facetState.facetGroups.allKeys.filter(
        (id) =>
            facetState.facetGroups.byKey[id]?.selectionType !== SelectionType.HIDDEN &&
            !!facetState.facetGroups.byKey[id]?.hibuFacetValues.length &&
            id.startsWith('metadata')
    );

    const showValue = (val: string, fieldName: string, key: string) => {
        let translationName = fieldName;
        if (fieldName.endsWith('_bool')) {
            translationName = 'boolean';
        }
        const translatedValue = translations.translations?.[translationName]?.[val];
        const value = translatedValue ? translatedValue : val;
        return (
            <EditableTag
                fieldType="line"
                key={key}
                content={value}
                fieldName={fieldName}
                editableLabel={value}
                allowEditing={false}
            />
        );
    };

    const showMetadata = (metadataName: string) => {
        const metadataValue = metadata[metadataName];
        if (metadataValue === undefined || metadataName === '') return;

        const fieldName = 'metadata.' + metadataName;
        const componentKey = 'metadata.' + metadataName + '-value';
        return (
            <div className="document-tag" key={metadataName}>
                <div className="tag-name cell">
                    {t('fieldTitle.metadata.' + metadataName + '.label', buildDefaultFieldTitle(metadataName))}
                </div>
                <div className="tag-value cell" key={'metadata' + metadataName}>
                    {Array.isArray(metadataValue) ? (
                        <>{metadataValue.map((value, i) => showValue(value, metadataName, componentKey + i))}</>
                    ) : (
                        showValue(metadataValue, fieldName, componentKey)
                    )}
                </div>
            </div>
        );
    };

    const showMetadataFromConfig = searchConfig.detailFields?.detailMetadata?.map((metadataName: string) => {
        return <>{showMetadata(metadataName)}</>;
    });

    const showMetadataFromFacets = metadataFacets.map((metadataFacet: string) => {
        // here we only consider the metadata, that is also present as a metadataFacet.
        // this is the default, if the detailFields array from the config is empty.
        const metadataName = metadataFacet.substring(metadataFacet.indexOf('.') + 1);
        return <>{showMetadata(metadataName)}</>;
    });

    return (
        <div className={classNames('document-infos', 'metadata-tags')}>
            {isEmptyArray(searchConfig.detailFields?.detailMetadata) ? showMetadataFromFacets : showMetadataFromConfig}
        </div>
    );
});
